import { Vector3 } from 'three';
import { hasInteriorView } from '../data/carModels';
import type { CarBounds, CarModel, Vector3Tuple } from '../types';

export const VIEW_MODES = {
  EXTERIOR: 'exterior',
  INTERIOR: 'interior',
} as const;

export type ViewMode = (typeof VIEW_MODES)[keyof typeof VIEW_MODES];

/** OrbitControls에 그대로 얹는 조작 한계. */
export interface OrbitControlLimits {
  minDistance: number;
  maxDistance: number;
  minPolarAngle: number;
  maxPolarAngle: number;
  enableZoom: boolean;
  rotateSpeed: number;
}

export interface CameraPose {
  position: Vector3Tuple;
  target: Vector3Tuple;
  fov: number;
  controls: OrbitControlLimits;
}

/**
 * 카메라 pose는 모델 실측 치수에서 계산합니다.
 *
 * 모델 네 대의 전장이 4.4m(718 Boxster)에서 4.79m(Model Y), 높이가 1.2m에서 1.65m까지
 * 차이 나기 때문에 고정 좌표를 쓰면 어떤 차는 프레임에 넘치고 어떤 차는 작게 박힙니다.
 * 그래서 아래 비율만 상수로 두고 실제 좌표는 치수에 곱해서 만듭니다.
 * 비율은 Taycan(전장 4.95m)에서 헤드리스 렌더로 프레이밍을 맞춰 뽑은 값입니다.
 */

// 타깃까지의 수평 거리를 전장의 몇 배로 둘지. Taycan 기준 hypot(5.9, 6.35) / 4.95 = 1.75
const ORBIT_DISTANCE_PER_LENGTH = 1.75;
// 3/4 앵글. 차량 정면(+X)에서 이 각도만큼 돌아간 자리에 섭니다.
const ORBIT_AZIMUTH = Math.PI / 4;
// 눈높이를 지붕의 몇 배로 둘지. 1보다 조금 크게 잡아 납작한 실루엣을 살립니다.
const EYE_HEIGHT_PER_ROOF = 1.33;
// 시선이 향하는 높이. 차체 중간을 보게 해서 바닥이 과하게 들어오지 않게 합니다.
const TARGET_HEIGHT_PER_ROOF = 0.395;

const EXTERIOR_FOV = 40;
// 줌 한계도 치수에 비례시킵니다. 안쪽은 차체를 파고들지 않을 만큼,
// 바깥쪽은 차가 점처럼 작아지지 않을 만큼.
const MIN_DISTANCE_PER_BODY_RADIUS = 1.48;
const MAX_DISTANCE_PER_LENGTH = 2.42;

export function createExteriorPose(bounds: CarBounds): CameraPose {
  const length = bounds.frontX - bounds.rearX;
  const orbitDistance = length * ORBIT_DISTANCE_PER_LENGTH;
  // 차량이 XZ 평면에서 차지하는 최대 반경. 이보다 가까우면 카메라가 차 안으로 들어갑니다.
  const bodyRadius = Math.hypot(bounds.frontX, bounds.halfWidth);

  return {
    position: [
      Math.cos(ORBIT_AZIMUTH) * orbitDistance,
      bounds.roofY * EYE_HEIGHT_PER_ROOF,
      Math.sin(ORBIT_AZIMUTH) * orbitDistance,
    ],
    target: [0, bounds.roofY * TARGET_HEIGHT_PER_ROOF, 0],
    fov: EXTERIOR_FOV,
    controls: {
      minDistance: bodyRadius * MIN_DISTANCE_PER_BODY_RADIUS,
      maxDistance: length * MAX_DISTANCE_PER_LENGTH,
      minPolarAngle: 0.15,
      maxPolarAngle: 1.52,
      enableZoom: true,
      rotateSpeed: 0.6,
    },
  };
}

/**
 * OrbitControls의 min/maxDistance를 같은 값으로 묶으면
 * 카메라가 타깃 주변 아주 작은 구면에서만 움직여 1인칭 둘러보기처럼 동작합니다.
 */
const INTERIOR_LOOK_DISTANCE = 0.1;
const INTERIOR_FOV = 65;
// 살짝 아래를 보게 해서 대시보드가 프레임에 들어오게 합니다.
const INTERIOR_LOOK_PITCH = -0.15;

/**
 * 운전석 시점. 눈높이(interiorEye)는 모델별 실측값이라 계산하지 않고 그대로 씁니다.
 * 실내는 좁아서 비율로 추정하면 시트나 도어를 관통하기 때문입니다.
 *
 * 차량 위치를 받지 않습니다. 주행 모드를 나가면 차가 항상 무대 중심으로 돌아오기
 * 때문입니다(CarDriver의 정리 effect 참고).
 *
 * @param interiorEye 운전자 눈높이 [x, y, z]
 */
export function createInteriorPose(interiorEye: Vector3Tuple): CameraPose {
  const eye = new Vector3().fromArray(interiorEye);
  // 전면이 +X입니다.
  const lookDirection = new Vector3(1, INTERIOR_LOOK_PITCH, 0).normalize();
  const target = eye.clone().addScaledVector(lookDirection, INTERIOR_LOOK_DISTANCE);

  return {
    // 카탈로그 배열을 그대로 넘기면 pose를 만진 쪽이 카탈로그를 바꿔 버립니다.
    position: [eye.x, eye.y, eye.z],
    target: [target.x, target.y, target.z],
    fov: INTERIOR_FOV,
    controls: {
      minDistance: INTERIOR_LOOK_DISTANCE,
      maxDistance: INTERIOR_LOOK_DISTANCE,
      minPolarAngle: 0.7,
      maxPolarAngle: 2.3,
      enableZoom: false,
      // 1인칭에서는 드래그 방향과 시선 방향이 반대여야 자연스럽습니다.
      rotateSpeed: -0.4,
    },
  };
}

/**
 * 보기 모드와 모델에 맞는 pose.
 *
 * 실내 눈높이가 없는 모델(외관 전용 에셋)에 내부 보기를 요청하면 외관으로 되돌립니다.
 * UI가 이미 버튼을 막고 있지만, pose 계산이 UI를 신뢰하면 안 되는 자리입니다.
 * 잘못하면 카메라가 아무것도 없는 좌표에 박혀 검은 화면이 됩니다.
 *
 * 주행 모드는 여기서 다루지 않습니다. 추격 카메라는 목표 pose로 한 번 이동하고 끝나는
 * 것이 아니라 매 프레임 차를 따라가야 해서, 이 모듈의 "정지된 pose" 모델에 맞지
 * 않습니다. three/chaseCamera가 카메라를 단독으로 소유하고 CameraRig는 물러납니다.
 *
 * @param viewMode ViewMode가 아닌 값이 오면 외관으로 되돌립니다.
 */
export function resolveCameraPose(viewMode: string, model: CarModel): CameraPose {
  if (viewMode === VIEW_MODES.INTERIOR && hasInteriorView(model)) {
    return createInteriorPose(model.interiorEye);
  }
  return createExteriorPose(model.bounds);
}
